import { Search } from 'lucide-react-native';
import { useCallback } from 'react';
import { twJoin } from 'tailwind-merge';

import type { NumberedPendingReference } from '../../model/numbering';
import {
  formatFindingSectionTitle,
  PENDING_REFERENCE_TITLE,
  PENDING_REFERENCE_TOAST_NOTE,
} from '../../model/referenceCopy';

import { ReferenceFootnoteRow } from '@/components/chat/bubbles/references/ReferenceFootnoteRow';
import type { MessageReference } from '@/components/chat/bubbles/references/messageReferences';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Pressable } from '@/components/ui/pressable';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useToaster } from '@/hooks/useToaster';
import { getAccessibilityProps } from '@/util/accessibilityProps';

interface StatementReferencesFooterProps {
  /** The author's pending references, numbered after the server's. */
  pending: readonly NumberedPendingReference[];
  /** Every reference the reader can open, in the server's order. */
  references: readonly MessageReference[];
  /** Bubble side — drives muted-vs-light text colors so footnote
   * metadata reads correctly on either background. */
  position: 'left' | 'right';
  onOpenReference: (reference: MessageReference) => void;
}

/**
 * Bottom-of-bubble footnote list rendered when an expert answer message
 * has one or more references. Both viewers see this — it is the
 * recipient's primary surface for what was cited, and the author sees it
 * as a sanity-check that the recipient view will render the right thing.
 *
 * Each reference is one line, `[N]` and the article's name, and opens the
 * message's reference sheet at that reference. The sheet carries the full
 * citation and the link out; the footer is only an index into it.
 *
 * Works attached to a statement and works only named or linked in the text
 * share one list, in the order the server numbered them, so a message has
 * one reference list rather than two.
 *
 * Grouping: when the author has pending (described, not-yet-attached)
 * references, the list splits into a quiet **Attached** group and a
 * louder **"Primum is finding"** group with a search-icon header — the
 * same split the statement sheet uses, so the in-progress state reads
 * clearly even at the bottom of the bubble. Pending entries are filtered
 * upstream for the recipient, so they only ever see attached rows; in
 * that case the headers are suppressed and the list renders flat.
 */
export function StatementReferencesFooter({
  pending,
  references,
  position,
  onOpenReference,
}: StatementReferencesFooterProps) {
  if (references.length === 0 && pending.length === 0) {
    return null;
  }

  return (
    <VStack space="xs" className="mt-2 min-w-0 border-t border-outline-200">
      {references.length > 0 ? (
        <VStack>
          {references.map((reference) => (
            <ReferenceFootnoteRow
              key={reference.number}
              reference={reference}
              position={position}
              onPress={onOpenReference}
            />
          ))}
        </VStack>
      ) : null}
      {pending.length > 0 ? (
        <VStack className="min-w-0">
          <FootnoteSectionHeader
            position={position}
            label={formatFindingSectionTitle(pending.length)}
          />
          {pending.map((entry) => (
            <PendingFootnoteRow key={entry.number} entry={entry} position={position} />
          ))}
        </VStack>
      ) : null}
    </VStack>
  );
}

/**
 * Uppercase "Primum is finding" group label inside the footer, marked
 * with a search icon to set the in-progress rows apart from the attached
 * rows above (which render unlabeled). Tint follows the bubble side so it
 * reads on either background.
 */
function FootnoteSectionHeader({ position, label }: { position: 'left' | 'right'; label: string }) {
  const tintClass = position === 'left' ? 'text-primary-700' : 'text-typography-0';

  return (
    <HStack space="xs" className="items-center pt-2">
      <Icon as={Search} size="2xs" className={tintClass} />
      <Text size="2xs" className={twJoin('font-bold uppercase tracking-wide', tintClass)}>
        {label}
      </Text>
    </HStack>
  );
}

/**
 * A reference the author described and Primum staff have not yet found:
 * the description in italics, with a toast on press explaining that only
 * the author sees it. Pending entries are filtered out for the recipient
 * upstream, so this row never renders to them.
 *
 * `flex-1` on the label, not `shrink`, is load-bearing on iOS: with
 * `shrink` the text reports its unwrapped intrinsic height before
 * `numberOfLines` applies, and the bubble grows to fit lines it never
 * draws.
 */
function PendingFootnoteRow({
  entry,
  position,
}: {
  entry: NumberedPendingReference;
  position: 'left' | 'right';
}) {
  const toast = useToaster();

  const handlePress = useCallback(() => {
    toast.showInfo({ title: PENDING_REFERENCE_TITLE, message: PENDING_REFERENCE_TOAST_NOTE });
  }, [toast]);

  const { description } = entry.reference;

  const tintClass = position === 'left' ? 'text-primary-600' : 'text-typography-0';

  return (
    <Pressable
      onPress={handlePress}
      className="min-w-0"
      {...getAccessibilityProps({
        label: `Reference ${entry.number}: ${description} — Primum is finding this source. Visible only to you until it's attached. Tap for details.`,
        role: 'button',
        hint: 'Shows a note explaining that pending references are visible only to you',
      })}>
      <HStack space="xs" className="min-h-8 min-w-0 items-center">
        <Text size="xs" className={twJoin('shrink-0 font-medium', tintClass)}>
          {`[${entry.number}]`}
        </Text>
        <Text
          numberOfLines={1}
          ellipsizeMode="tail"
          className={twJoin('min-w-0 flex-1 font-medium text-sm italic', tintClass)}>
          {description}
        </Text>
      </HStack>
    </Pressable>
  );
}
